import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useAuth } from "@/contexts/auth-context";
import { ssoService } from "@/services/sso-service";

// Entra ID PKCE login shared by the login screen (moved from the old login page).
// Desktop opens a popup that relays the code back; mobile (and the local mock flow when popups are
// unwanted) uses a same-tab redirect flagged in sessionStorage.
const SSO_REDIRECT_FLAG = "sso_redirect_flow";
const SSO_LOGIN_TYPE = "sso_login_type";
const SSO_RETURN_TO = "sso_return_to";
// 사전 초대 가입(slide 95): Entra 로그인으로 신원(id_token)만 확인하고 Navigator에는 로그인하지 않는 모드
const SSO_IDENTITY_ONLY = "sso_identity_only";
const INVITE_IDENTITY = "invite_entra_token";

export const readInviteIdentity = () => sessionStorage.getItem(INVITE_IDENTITY);
export const storeInviteIdentity = (token: string) => sessionStorage.setItem(INVITE_IDENTITY, token);
export const clearInviteIdentity = () => sessionStorage.removeItem(INVITE_IDENTITY);

export type SsoLoginType = "outlook" | "pwc_sso";

const isMobileDevice = () =>
  typeof navigator !== "undefined" && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

const preferRedirect = () => isMobileDevice() || (import.meta.env.DEV && import.meta.env.VITE_MOCK_SSO === "true");

const providerErrorMessage = (error: string | null, description: string | null) =>
  error === "access_denied"
    ? "아직 이용 권한이 없는 계정입니다. 관리자 승인 후 로그인할 수 있습니다."
    : typeof description === "string"
      ? description.replace(/[\r\n]/g, "")
      : "로그인에 실패했습니다. 다시 시도해 주세요.";

interface UseSsoLoginOptions {
  // Called after tokens are stored and the user is loaded; receives the path to return to
  onSuccess: (returnTo: string) => void;
  // Called with the raw error (axios error from /auth/sso/login or a provider error message)
  onError: (error: unknown) => void;
}

export function useSsoLogin({ onSuccess, onError }: UseSsoLoginOptions) {
  const [searchParams] = useSearchParams();
  const { handleSsoCallback } = useAuth();
  const code = searchParams.get("code");
  const providerError = searchParams.get("error");
  const isCallback = !!(code || providerError);
  const [isRedirectFlow] = useState(
    () => isCallback && !window.opener && sessionStorage.getItem(SSO_REDIRECT_FLAG) === "true",
  );
  const [processing, setProcessing] = useState(isRedirectFlow);
  const handledRef = useRef(false);

  const exchange = useCallback(
    async (authCode: string) => {
      setProcessing(true);
      try {
        const tokens = await ssoService.exchangeToken(authCode);
        if (sessionStorage.getItem(SSO_IDENTITY_ONLY) === "true") {
          sessionStorage.removeItem(SSO_IDENTITY_ONLY);
          if (tokens.access_token) storeInviteIdentity(tokens.access_token);
        } else if (tokens.access_token) {
          await handleSsoCallback(tokens.access_token, tokens.refresh_token);
        }
        const returnTo = sessionStorage.getItem(SSO_RETURN_TO) || "/";
        sessionStorage.removeItem(SSO_RETURN_TO);
        onSuccess(returnTo);
      } catch (err) {
        onError(err);
      } finally {
        setProcessing(false);
      }
    },
    [handleSsoCallback, onSuccess, onError],
  );

  // Callback tab: redirect flow exchanges inline, popup flow relays to the opener and closes.
  useEffect(() => {
    if (!isCallback || handledRef.current) return;
    handledRef.current = true;
    const errorDescription = searchParams.get("error_description");

    if (isRedirectFlow) {
      sessionStorage.removeItem(SSO_REDIRECT_FLAG);
      if (code) exchange(code);
      else {
        setProcessing(false);
        onError(new Error(providerErrorMessage(providerError, errorDescription)));
      }
      return;
    }

    const payload = { type: "sso-callback", code, error: providerError, error_description: errorDescription };
    if (window.opener) {
      window.opener.postMessage(payload, window.location.origin);
    } else {
      const channel = new BroadcastChannel("sso-login");
      channel.postMessage(payload);
      channel.close();
    }
    window.close();
  }, [isCallback, isRedirectFlow, code, providerError, searchParams, exchange, onError]);

  const startLogin = useCallback(
    async (loginType: SsoLoginType, returnTo: string, options?: { identityOnly?: boolean }) => {
      sessionStorage.setItem(SSO_LOGIN_TYPE, loginType);
      sessionStorage.setItem(SSO_RETURN_TO, returnTo);
      if (options?.identityOnly) sessionStorage.setItem(SSO_IDENTITY_ONLY, "true");
      else sessionStorage.removeItem(SSO_IDENTITY_ONLY);
      let url: string;
      try {
        url = await ssoService.getLoginUrl();
      } catch (err) {
        onError(err);
        return;
      }

      if (preferRedirect()) {
        sessionStorage.setItem(SSO_REDIRECT_FLAG, "true");
        window.location.assign(url);
        return;
      }

      const popup = window.open(url, "sso-login", "width=500,height=700,left=200,top=100");
      if (!popup) {
        onError(new Error("팝업이 차단되었습니다. 팝업 차단을 해제해 주세요."));
        return;
      }

      let handled = false;
      const channel = new BroadcastChannel("sso-login");
      const cleanup = () => {
        window.removeEventListener("message", onMessage);
        channel.close();
      };
      const handle = (data: { type?: string; code?: string; error?: string; error_description?: string }) => {
        if (data?.type !== "sso-callback" || handled) return;
        handled = true;
        cleanup();
        try {
          popup.close();
        } catch {
          /* already closed */
        }
        if (data.code) exchange(data.code);
        else onError(new Error(providerErrorMessage(data.error ?? null, data.error_description ?? null)));
      };
      const onMessage = (event: MessageEvent) => {
        if (event.origin === window.location.origin) handle(event.data);
      };
      channel.onmessage = (event) => handle(event.data);
      window.addEventListener("message", onMessage);
      // Microsoft's COOP headers break popup.closed, so give up listening after 5 minutes instead.
      setTimeout(() => !handled && cleanup(), 5 * 60 * 1000);
    },
    [exchange, onError],
  );

  return { isCallback, isRedirectFlow, processing, startLogin };
}
